import DataGrid, {
    Column,
    Pager,
    Paging,
    SearchPanel,
    Summary,
    TotalItem
} from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import React, { useCallback, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import { HasPermission } from 'Layout/Permissions/permissionsCache';
import { getAllEcomSalesOrdersListData } from '../services/ecomSalesOrders.service';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ViewEcomSalesOrderItems from './ViewEcomSalesOrderItems';
import { encryptItem } from 'helpers/secureNavigate';
import { useNavigate } from 'react-router-dom';

const EcomSalesOrdersList: React.FC = () => {
    const navigate = useNavigate()
    const [refreshKey, setRefreshKey] = useState(0);

    const dataSource = useMemo(() => {
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    const search = JSON.parse(JSON.stringify(loadOptions));
                    const parameterList = new URLSearchParams();

                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }

                    const response = await getAllEcomSalesOrdersListData(parameterList);
                    return response;
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            }
        });
    }, []);

    const [isOpen, setIsOpen] = useState(false)
    const [selectedId, setSelectedId] = useState<number>()

    const renderActionButtons = useCallback((cellData: any) => (
        <div className='d-flex gap-2'>
            {HasPermission("EcomSalesOrder.Write") && <>
                <button
                    className='btn btn-sm action-btn'
                    onClick={() => {
                        setIsOpen(true)
                        setSelectedId(cellData?.data?.id)
                    }}
                >
                    <FontAwesomeIcon icon={faEye} />
                </button>

                {!!cellData?.data?.hasCustomProduct && <button
                    className='btn btn-sm add-btn'
                    onClick={() => {
                        navigate(`/assign-product/${encryptItem(cellData?.data?.id)}`);
                    }}
                >
                    Assign Product
                </button>
                }
            </>}
        </div>
    ), []);


    const reloadDataSource = () => {
        setRefreshKey((prev) => prev + 1);
    };


    return (
        <React.Fragment>

            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0'>E-commerce Sales Orders</h5>
                        </Card.Header>

                        {HasPermission("EcomSalesOrder.Read") &&
                            <Card.Body className="pt-0">
                                <DataGrid
                                    dataSource={dataSource}
                                    remoteOperations={true}
                                    showRowLines={true}
                                    keyExpr="id"
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    key={refreshKey}
                                    onContentReady={(e) => {
                                        const hasData = e.component.totalCount() > 0;
                                        e.component.option("pager.visible", hasData);
                                        e.component.option("paging.visible", hasData);
                                    }}
                                >

                                    <Pager
                                        allowedPageSizes={[10, 25, 50, 100]}
                                        showPageSizeSelector={true}
                                        showNavigationButtons={true}
                                        displayMode="full"
                                        showInfo={true}
                                    />

                                    <Paging defaultPageSize={10} />

                                    <SearchPanel
                                        visible={true}
                                        width={240}
                                        placeholder="Search..."
                                    />

                                    <Summary>
                                        <TotalItem
                                        />
                                    </Summary>

                                    <Column
                                        dataField="action"
                                        caption="Action"
                                        dataType="string"
                                        cellRender={renderActionButtons}
                                        allowFiltering={false}
                                    />

                                    <Column
                                        dataField="id"
                                        dataType="number"
                                        sortOrder="desc"
                                        visible={false}
                                    />

                                    <Column
                                        dataField="invoiceName"
                                        caption="Invoice No."
                                        allowFiltering={true}
                                    />

                                    <Column
                                        dataField="invoiceDate"
                                        caption="Invoice Date"
                                        dataType="date"
                                        format="dd/MM/yyyy"
                                        allowFiltering={true}
                                    />

                                    <Column
                                        dataField="customerName"
                                        caption="Customer Name"
                                        allowFiltering={true}
                                    />

                                    <Column
                                        dataField="mobileNo"
                                        caption="Mobile No."
                                        allowFiltering={true}
                                    />

                                    <Column
                                        dataField="salesPersonName"
                                        caption="Sales Person"
                                        allowFiltering={true}
                                    />

                                    <Column
                                        dataField="totalAmount"
                                        caption="Amount"
                                        format="#,##0.00"
                                        allowFiltering={true}
                                    />

                                    <Column
                                        allowFiltering={false}
                                        caption="Product Shipment Done"
                                        cssClass="text-center"
                                        cellRender={({ data }) => (
                                            <span>{data?.isDone ? "Yes" : "No"}</span>
                                        )}
                                    />
                                </DataGrid>

                            </Card.Body>
                        }
                    </Card>
                </div>
            </div>

            <ViewEcomSalesOrderItems
                isOpen={isOpen}
                selectedId={selectedId}
                onClose={() => {
                    setSelectedId(undefined)
                    setIsOpen(false)
                }}
                reloadDataSource={reloadDataSource}
            />
        </React.Fragment>
    );
};

export default EcomSalesOrdersList;